import { useConfig, useNotification, useOidcWithAdmin } from '@revenuewell/front-end-bundle';
import { Contact, ContactApi } from 'apps/wrapper/src/services/api-client/contact-api';
import AppointmentService from 'apps/wrapper/src/services/appointment/appointment-service';
import { AppointmentModificationDetails } from 'apps/wrapper/src/services/appointment/types/appointment-modifications';
import { AppointmentRequestDetails } from 'apps/wrapper/src/services/appointment/types/appointment-request-details';
import { AppointmentRequestStatus } from 'apps/wrapper/src/services/appointment/types/appointment-request-status';
import { FC, ReactNode, useEffect, useMemo, useState } from 'react';
import { useApptRequestsWidget } from '../use-appt-requests-widget';
import { RescheduleRequestViewContext } from './context';

export interface RescheduleRequestViewProviderProps {
  apptRequestId: number;
  children: ReactNode;
}

export const RescheduleRequestViewProvider: FC<RescheduleRequestViewProviderProps> = (props) => {
  const { apptRequestId } = props;
  const { config } = useConfig();
  const { showError } = useNotification();
  const { oidcService } = useOidcWithAdmin();
  const { locationId } = useApptRequestsWidget();
  const { goBack } = useApptRequestsWidget();
  const [isLoading, setIsLoading] = useState(true);
  const [contact, setContact] = useState<Contact>();
  const [requestDetails, setRequestDetails] = useState<AppointmentRequestDetails>(null!);
  const [originalAppointment, setOriginalAppointment] = useState<AppointmentModificationDetails>();
  const [tempAppointment, setTempAppointment] = useState<AppointmentModificationDetails>();
  const appointmentService = useMemo(() => new AppointmentService(config, oidcService), [config, oidcService]);
  const contactApi = ContactApi.getInstance();

  const isEditable = useMemo(() => {
    if (!requestDetails) return false;
    return (
      requestDetails.status === AppointmentRequestStatus.New ||
      requestDetails.status === AppointmentRequestStatus.Pending
    );
  }, [requestDetails]);

  useEffect(() => {
    const init = async () => {
      setIsLoading(true);

      const [apptRequestDetails, appointmentModifications] = await Promise.all([
        loadApptRequestDetails(apptRequestId, locationId),
        appointmentService.getAppointmentModifications(apptRequestId, locationId)
      ]);

      if (!apptRequestDetails || !appointmentModifications) {
        goBack();
        showError('Unable to load appointment request details');
        return;
      }

      if (apptRequestDetails.patient.id) {
        const contactDetails = await contactApi.getContactDetailsById(apptRequestDetails.patient.id);
        setContact(contactDetails);
      }

      setOriginalAppointment(appointmentModifications.originalAppointment);
      setTempAppointment(appointmentModifications.tempAppointment);
      setRequestDetails(apptRequestDetails);
      setIsLoading(false);
    };

    init();
  }, [apptRequestId, locationId]);

  const loadApptRequestDetails = async (apptReqId: number, locId: number) => {
    try {
      return await appointmentService.getAppointmentRequestDetails(apptReqId, locId);
    } catch {
      showError('Unable to load appointment request details');

      return null;
    }
  };

  const contextValue = useMemo(
    () => ({
      isLoading,
      contact,
      isEditable,
      requestDetails,
      originalAppointment,
      tempAppointment
    }),
    [isLoading, contact, isEditable, requestDetails, originalAppointment, tempAppointment]
  );

  return (
    <RescheduleRequestViewContext.Provider value={contextValue}>{props.children}</RescheduleRequestViewContext.Provider>
  );
};
